import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { BaseChartProps } from '../../types/charts.types';
import { Line } from 'react-chartjs-2';
import { MdOutlineRefresh } from 'react-icons/md';
import { TbZoomReset } from 'react-icons/tb';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

export const BaseChart = ({
  labels,
  datasets,
  isLoading = false,
  isError = false,
  onRefresh,
  title
}: BaseChartProps) => {
  const { t } = useTranslation();
  const chartData = {
    labels,
    datasets: datasets.map((dataset) => ({
      fill: true,
      tension: 0.1,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHitRadius: 5,
      ...dataset
    }))
  };
  const chartOptions = {
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: 'x' as const,
          modifierKey: 'alt' as const
        },
        zoom: {
          wheel: {
            enabled: true
          },
          drag: {
            enabled: true
          },
          pinch: {
            enabled: true
          },
          mode: 'x' as const,
          scaleMode: 'xy' as const
        }
      }
    }
  };
  const chartRef = useRef<ChartJS<'line'>>(null);

  const onZoomReset = () => {
    chartRef.current?.resetZoom();
  };

  return (
    <div className="bg-surface-base rounded-[16px] border border-border-dark/[0.11] p-[24px] mb-[16px] shadow-sm">
      <div className="flex justify-between items-center mb-[16px]">
        <h3 className="text-[16px] font-semibold text-content-dark">{title}</h3>
        <div className="flex items-center gap-[4px] relative">
          <button
            onClick={onZoomReset}
            disabled={isLoading}
            className="px-[12px] py-[6px] rounded-[8px] text-[12px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 active:bg-blue-deep-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center"
          >
            <TbZoomReset size={20} className="inline-block" />
          </button>
          <button
            onClick={() => {
              onZoomReset?.();
              onRefresh?.();
            }}
            disabled={isLoading}
            className="px-[12px] py-[6px] rounded-[8px] text-[12px] font-medium bg-blue text-surface-base hover:bg-blue-deep-1 active:bg-blue-deep-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-[12px] w-[12px] border-b-2 border-surface-base" />
                {t('admin.charts.refreshing')}
              </>
            ) : (
              <MdOutlineRefresh size={20} className="inline-block" />
            )}
          </button>
        </div>
      </div>
      <div className="h-[300px]">
        {/* The chart's own title sits directly above these, so the state
            messages don't interpolate it: lower-casing a translated title is
            wrong in German (nouns are capitalised) and the word order of
            "Loading X data" doesn't survive translation intact. */}
        {isLoading ? (
          <div className="flex justify-center items-center h-full text-gray-500">
            <div className="animate-spin rounded-full h-[12px] w-[12px] border-b-2 border-gray-500 mr-3" />
            {t('admin.charts.loading')}
          </div>
        ) : isError ? (
          <div className="flex justify-center items-center h-full text-red-500">
            {t('admin.charts.error')}
          </div>
        ) : labels.length === 0 ? (
          <div className="flex justify-center items-center h-full text-gray-500">
            {t('admin.charts.empty')}
          </div>
        ) : (
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};
